/**
 * Resource disposal. ARCHITECTURE.md §11.2.
 *
 * Duck-typed so this module stays three-free: anything with a `dispose()` works.
 * Every system holds one bag and calls `disposeAll()` in its own `dispose()`.
 */

export class DisposalBag {
  constructor(label = 'bag') {
    this.label = label;
    this._geometries = new Set();
    this._materials = new Set();
    this._textures = new Set();
    this._targets = new Set();
    this._audioNodes = new Set();
    this._listeners = [];
    this._customs = [];
    this._disposed = false;
    this.counts = { geometry: 0, material: 0, texture: 0, target: 0, audio: 0, listener: 0, custom: 0 };
  }

  geometry(g) {
    if (g) this._geometries.add(g);
    return g;
  }

  material(m) {
    if (!m) return m;
    if (Array.isArray(m)) {
      for (const x of m) this.material(x);
      return m;
    }
    this._materials.add(m);
    return m;
  }

  texture(t) {
    if (t) this._textures.add(t);
    return t;
  }

  target(rt) {
    if (rt) this._targets.add(rt);
    return rt;
  }

  audioNode(n) {
    if (n) this._audioNodes.add(n);
    return n;
  }

  /** Registers a DOM/Window event listener for automatic removal. */
  listener(target, type, fn, opts) {
    if (!target || !target.addEventListener) return fn;
    target.addEventListener(type, fn, opts);
    this._listeners.push({ target, type, fn, opts });
    return fn;
  }

  /** Registers an arbitrary teardown callback. Runs last, in LIFO order. */
  custom(fn) {
    if (typeof fn === 'function') this._customs.push(fn);
    return fn;
  }

  /**
   * Recursively registers everything reachable from a three.js Object3D:
   * geometries, materials, and every texture referenced by those materials.
   */
  object3D(root) {
    if (!root || typeof root.traverse !== 'function') return root;
    root.traverse((node) => {
      if (node.geometry) this.geometry(node.geometry);
      if (node.material) {
        const mats = Array.isArray(node.material) ? node.material : [node.material];
        for (const mat of mats) {
          this.material(mat);
          for (const key in mat) {
            const v = mat[key];
            if (v && v.isTexture) this.texture(v);
          }
        }
      }
    });
    return root;
  }

  get size() {
    return (
      this._geometries.size +
      this._materials.size +
      this._textures.size +
      this._targets.size +
      this._audioNodes.size +
      this._listeners.length +
      this._customs.length
    );
  }

  get disposed() {
    return this._disposed;
  }

  /** Idempotent. Safe to call from a dispose() that may run twice. */
  disposeAll() {
    if (this._disposed) return this.counts;

    for (const t of this._textures) {
      if (t.dispose) t.dispose();
      this.counts.texture++;
    }
    for (const m of this._materials) {
      if (m.dispose) m.dispose();
      this.counts.material++;
    }
    for (const g of this._geometries) {
      if (g.dispose) g.dispose();
      this.counts.geometry++;
    }
    for (const rt of this._targets) {
      if (rt.dispose) rt.dispose();
      this.counts.target++;
    }
    for (const n of this._audioNodes) {
      try {
        if (n.stop) n.stop();
      } catch {
        /* already stopped */
      }
      if (n.disconnect) n.disconnect();
      this.counts.audio++;
    }
    for (const { target, type, fn, opts } of this._listeners) {
      target.removeEventListener(type, fn, opts);
      this.counts.listener++;
    }
    for (let i = this._customs.length - 1; i >= 0; i--) {
      this._customs[i]();
      this.counts.custom++;
    }

    this._geometries.clear();
    this._materials.clear();
    this._textures.clear();
    this._targets.clear();
    this._audioNodes.clear();
    this._listeners.length = 0;
    this._customs.length = 0;
    this._disposed = true;
    return this.counts;
  }
}

/**
 * Detaches an Object3D from its parent and disposes its whole subtree.
 * Convenience for destructibles and round-scoped props.
 */
export function disposeObject3D(root) {
  if (!root) return;
  const bag = new DisposalBag('adhoc');
  bag.object3D(root);
  if (root.parent) root.parent.remove(root);
  bag.disposeAll();
}
